<template>
  <div class="chatbot-list">
    <!-- Utilisation du composant Notification -->
    <Notification 
      v-model:show="showNotification" 
      :message="notificationMessage" 
      :type="notificationType"
    />

    <PageHeader 
      :pageName="$t('chatbotList.pageName')" 
      :pageTitle="$t('chatbotList.pageTitle')" 
      :pageSubtitle="$t('chatbotList.pageSubtitle')"
    >
      <template #actions>
        <button
          v-if="!scopedSession"
          class="sleek-add-btn"
          :class="{ 'disabled': agentLimitReached }"
          @click="handleNewAgent"
          :title="agentLimitReached ? $t('chatbotList.limitReached', { max: maxAgents }) : $t('chatbotList.createNew')"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>{{ $t('chatbotList.newAgent') }}</span>
          <span v-if="agentLimitReached" class="limit-badge-small">{{ chatbots.length }}/{{ maxAgents }}</span>
        </button>
      </template>
    </PageHeader>

    <!-- Dev Dump Display -->
    <div v-if="isDev && showUserDump" style="background: #1f2937; color: #60a5fa; padding: 1rem; margin: 0 0 1rem 0; border-radius: 8px; overflow: auto; max-height: 500px; font-family: monospace; position: relative;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem; border-bottom: 1px solid #374151; padding-bottom: 0.5rem;">
            <strong style="color: #e5e7eb;">localStorage.getItem('user')</strong>
            <button @click="copyToClipboard(JSON.stringify(userDumpData, null, 2))" style="background: rgba(255,255,255,0.1); border: 1px solid #374151; color: white; padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.8rem;">Copier JSON</button>
        </div>
        <pre style="margin: 0; white-space: pre-wrap;">{{ JSON.stringify(userDumpData, null, 2) }}</pre>
    </div>

    <!-- Search and Filter -->
    <div class="search-filter-section">
      <div class="search-box-wrapper">
        <div class="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="$t('chatbotList.searchPlaceholder')"
          >
        </div>
      </div>
      
      <div class="filter-options-wrapper">
        <div class="filter-actions">
           <!-- Dev Dump User Button (discret) -->
           <button v-if="isDev" @click="showUserDump = !showUserDump" class="dev-dump-btn" title="DUMP USER">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </button>

          <select v-model="statusFilter" class="status-select">
            <option value="all">{{ $t('chatbotList.statusAll') }}</option>
            <option value="active">{{ $t('chatbotList.statusActive') }}</option>
            <option value="inactive">{{ $t('chatbotList.statusInactive') }}</option>
            <option value="maintenance">{{ $t('chatbotList.statusMaintenance') }}</option>
          </select>
          
          <button class="sleek-sort-btn" @click="toggleSortOrder" :title="sortOrder === 'desc' ? $t('chatbotList.sortNewest') : $t('chatbotList.sortOldest')">
            <svg v-if="sortOrder === 'desc'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 4h13M3 8h9M3 12h5M21 10V20M17 16l4 4 4-4"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 4h13M3 8h9M3 12h5M21 14V4M17 8l4-4 4 4"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Chatbots Grid -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <p>{{ $t('chatbotList.loading') }}</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4M12 16h.01"/>
      </svg>
      
      <p>{{ error }}</p>
      <button v-if="error.includes('reconnecter')" class="retry-btn" @click="router.push('/login')">
        {{ $t('chatbotList.reconnect') }}
      </button>
      <button v-else class="retry-btn" @click="fetchChatbots">
        {{ $t('chatbotList.retry') }}
      </button>
    </div>
    
    <div v-else>
      <div class="chatbots-grid sleek-grid">
        <div 
          v-for="chatbot in displayedChatbots" 
          :key="chatbot._id" 
          class="chatbot-card sleek-card" 
          @click="openChatbot(chatbot, $event)"
          :class="{ 'whatsapp-theme': chatbot.type === 'whatsapp' || chatbot.appName === 'Griot' }"
        >
          <!-- Card Cover -->
          <div class="card-cover" :style="{ backgroundImage: 'url(/assets/card-pattern.png)' }">
            <div class="card-pattern"></div>
            
            <!-- Menu Button (Top Right) -->
            <div class="card-actions-top">
              <button class="menu-btn glass-btn" @click.stop="toggleMenu(chatbot._id)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="1"/>
                  <circle cx="19" cy="12" r="1"/>
                  <circle cx="5" cy="12" r="1"/>
                </svg>
              </button>
              
              <div v-if="activeMenu === chatbot._id" class="chatbot-menu" @click.stop>
                <button class="menu-item" @click="editChatbot(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  <span>{{ $t('chatbotList.editSettings') }}</span>
                </button>
                <button v-if="!scopedSession" class="menu-item" @click="confirmClone(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  <span>{{ $t('chatbotList.cloneAgent') }}</span>
                </button>
                <button v-if="!delegatedSession" class="menu-item" @click="inviteCollaborator(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/>
                    <line x1="16" y1="11" x2="22" y2="11"/>
                  </svg>
                  <span>Inviter un collaborateur</span>
                </button>
                <button v-if="!scopedSession" class="menu-item delete" @click="confirmDelete(chatbot)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  <span>{{ $t('chatbotList.delete') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Avatar (Overlapping) -->
          <div class="card-avatar-wrapper">
             <div class="card-avatar">
               <img v-if="getChatbotAvatar(chatbot)" :src="getChatbotAvatar(chatbot)" :alt="chatbot.bot_name" class="avatar-img" />
               <div v-else class="avatar-fallback" :class="{ 'whatsapp': chatbot.type === 'whatsapp' }">
                  <svg v-if="chatbot.type === 'whatsapp'" width="24" height="24" viewBox="0 0 24 24" fill="white">
                     <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="10" rx="2"/>
                    <circle cx="12" cy="5" r="2"/>
                    <path d="M12 7v4"/>
                  </svg>
               </div>
             </div>
          </div>

          <!-- Content -->
          <div class="card-body">
             <div class="card-title-row">
                <h3>{{ chatbot.bot_name }}</h3>
                <span class="deploy-badge" :class="isDeployed(chatbot) ? 'online' : 'draft'">
                  <span class="deploy-dot"></span>
                  {{ isDeployed(chatbot) ? $t('chatbotList.online') : $t('chatbotList.draft') }}
                </span>
                <span class="status-badge-sleek" :class="chatbot.status">
                  {{ chatbot.status === 'active' ? $t('chatbotList.statusActive') : $t('chatbotList.statusInactive') }}
                </span>
             </div>
             
             <p class="card-desc">{{ truncateDescription(chatbot.description) }}</p>
             
             <div class="card-tags" v-if="chatbot.type">
                <span class="tag" :class="{ 'whatsapp-tag': chatbot.type === 'whatsapp' }">
                   {{ chatbot.type === 'whatsapp' ? 'WhatsApp' : chatbot.type }}
                </span>
             </div>
          </div>

          <!-- Footer -->
          <div class="card-footer">
             <button class="config-btn" @click.stop="openChatbot(chatbot, $event)">
               {{ $t('chatbotList.open') }}
             </button>

             <button
               class="deploy-btn"
               :class="{ 'is-online': isDeployed(chatbot) }"
               :disabled="deployingId === chatbot._id"
               @click.stop="toggleDeploy(chatbot)"
               :title="isDeployed(chatbot) ? $t('chatbotList.removeTitle') : $t('chatbotList.deployTitle')"
             >
               <span v-if="deployingId === chatbot._id">…</span>
               <template v-else>{{ isDeployed(chatbot) ? $t('chatbotList.remove') : $t('chatbotList.deploy') }}</template>
             </button>
             
             <div class="footer-stats-group">
                <div class="footer-stat" :title="$t('chatbotList.users')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>{{ formatNumber(chatbot.userCount || 0) }}</span>
                </div>
                <div class="footer-divider"></div>
                <div class="footer-stat" :title="$t('chatbotList.conversations')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                     <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>{{ formatNumber(chatbot.interactionCount || 0) }}</span>
                </div>
                
                <div v-if="chatbot.phone_number" class="footer-divider"></div>
                <div v-if="chatbot.phone_number" class="footer-stat connected" :title="$t('chatbotList.connected')">
                   <div class="connected-dot"></div>
                </div>
             </div>
          </div>
        </div>
      </div>

      <!-- Infinite Scroll Observer -->
      <div v-if="hasMoreChatbots" class="infinite-scroll-observer" ref="observerElement">
        <div class="loading-spinner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="60">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
      </div>

      <!-- Agents PARTAGÉS AVEC MOI (invitations limitées à des agents d'autres comptes).
           Visible uniquement dans MA propre session : cliquer bascule en session déléguée. -->
      <div v-if="sharedAgents.length" class="shared-section">
        <div class="shared-head">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <h3>Partagés avec moi</h3>
          <span class="shared-count">{{ sharedAgents.length }}</span>
        </div>
        <p class="shared-sub">Ces agents appartiennent à d'autres comptes : vous y collaborez sans consommer vos propres crédits.</p>
        <div class="shared-grid">
          <button
            v-for="s in sharedAgents" :key="s.account_id + '-' + s.bot._id"
            type="button" class="shared-card" :disabled="openingShared === s.bot._id"
            @click="openSharedAgent(s)"
          >
            <div class="shared-avatar-wrap">
              <div class="shared-avatar">
                <img v-if="getChatbotAvatar(s.bot)" :src="getChatbotAvatar(s.bot)" :alt="s.bot.bot_name" />
                <span v-else>{{ (s.bot.bot_name || '?').charAt(0).toUpperCase() }}</span>
              </div>
              <span class="shared-status-dot" :class="s.bot.status === 'active' ? 'active' : 'inactive'"></span>
            </div>
            <div class="shared-info">
              <span class="shared-name">{{ s.bot.bot_name }}</span>
              <span class="shared-owner">Partagé par {{ s.account_name }}</span>
            </div>
            <span class="shared-open">{{ openingShared === s.bot._id ? 'Ouverture…' : 'Ouvrir' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Add Chatbot Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('chatbotList.newAgent') }}</h3>
          <button class="close-btn" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('chatbotList.nameLabel') }}</label>
            <input v-model="newChatbot.name" type="text" :placeholder="$t('chatbotList.namePlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('chatbotList.descriptionLabel') }}</label>
            <textarea v-model="newChatbot.description" :placeholder="$t('chatbotList.descriptionPlaceholder')"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">{{ $t('chatbotList.cancel') }}</button>
          <button class="save-btn" @click="addChatbot">{{ $t('chatbotList.create') }}</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('chatbotList.deleteTitle') }}</h3>
          <button class="close-btn" @click="showDeleteModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('chatbotList.deleteConfirmText') }}</p>
          <div class="form-group">
            <label>{{ $t('chatbotList.deleteTypeLabel') }}</label>
            <input 
              v-model="deleteConfirmation" 
              type="text" 
              placeholder="SUPPRIMER"
              :class="{ 'error': deleteConfirmation && deleteConfirmation !== 'SUPPRIMER' }"
            >
            <span v-if="deleteConfirmation && deleteConfirmation !== 'SUPPRIMER'" class="error-message">
              {{ $t('chatbotList.deleteTypeError') }}
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteModal = false">{{ $t('chatbotList.cancel') }}</button>
          <button 
            class="delete-btn" 
            @click="deleteChatbot" 
            :disabled="deleteConfirmation !== 'SUPPRIMER'"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Chatbot Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('chatbotList.editTitle') }}</h3>
          <button class="close-btn" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('chatbotList.nameLabel') }}</label>
            <input v-model="editingChatbot.bot_name" type="text" :placeholder="$t('chatbotList.namePlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('chatbotList.appIdLabel') }}</label>
            <input v-model="editingChatbot.app_id" type="text" :placeholder="$t('chatbotList.appIdPlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('chatbotList.avatarUrlLabel') }}</label>
            <input v-model="editingChatbot.avatar" type="text" :placeholder="$t('chatbotList.avatarUrlPlaceholder')">
          </div>
          <div class="form-group">
            <label>{{ $t('chatbotList.descriptionLabel') }}</label>
            <textarea v-model="editingChatbot.description" :placeholder="$t('chatbotList.descriptionPlaceholder')"></textarea>
          </div>
          <div class="form-group">
            <label>{{ $t('chatbotList.statusLabel') }}</label>
            <select v-model="editingChatbot.status">
              <option value="active">{{ $t('chatbotList.statusActive') }}</option>
              <option value="inactive">{{ $t('chatbotList.statusInactive') }}</option>
              <option value="maintenance">{{ $t('chatbotList.statusMaintenance') }}</option>
            </select>
          </div>
          <div class="advanced-options">
            <router-link 
              :to="{ name: 'ChatbotSettings', params: { id: editingChatbot._id }}" 
              class="chatbot-link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span>{{ $t('chatbotList.advancedSettings') }}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </router-link>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">{{ $t('chatbotList.cancel') }}</button>
          <button class="save-btn" @click="saveChatbot">{{ $t('chatbotList.save') }}</button>
        </div>
      </div>
    </div>

    <!-- Clone Chatbot Modal -->
    <div v-if="showCloneModal" class="modal-overlay" @click="showCloneModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('chatbotList.cloneTitle') }}</h3>
          <button class="close-btn" @click="showCloneModal = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('chatbotList.cloneIntro') }} "<strong>{{ chatbotToClone?.bot_name }}</strong>"</p>
          <div class="form-group">
            <label>{{ $t('chatbotList.cloneNameLabel') }}</label>
            <input 
              v-model="cloneName" 
              type="text" 
              :placeholder="$t('chatbotList.cloneNamePlaceholder')"
              :class="{ 'error': cloneName && cloneName.trim() === '' }"
            >
            <span v-if="cloneName && cloneName.trim() === ''" class="error-message">
              {{ $t('chatbotList.cloneNameEmpty') }}
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showCloneModal = false">{{ $t('chatbotList.cancel') }}</button>
          <button 
            class="save-btn" 
            @click="cloneChatbot" 
            :disabled="!cloneName || cloneName.trim() === '' || isCloning"
          >
            <span v-if="!isCloning">{{ $t('chatbotList.clone') }}</span>
            <span v-else>{{ $t('chatbotList.cloning') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !error && displayedChatbots.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      <h3>{{ $t('chatbotList.emptyTitle') }}</h3>
      <p>{{ $t('chatbotList.emptyText') }}</p>
      <button class="create-btn" @click="$router.push('/dashboard/chatbots/create')"  >{{ $t('chatbotList.emptyCreate') }}</button>
    </div>

    <!-- Community Suggestion Bubble - REPLACED BY WhatsAppBubble -->

    <!-- Notification -->

    <!-- Upgrade Modal for subscription gating -->
    <UpgradeModal 
      :show="showUpgradeModal" 
      :current-plan-slug="userSubscription?.slug || null"
      @close="showUpgradeModal = false"
      @selectPlan="handlePlanSelect"
    />
    
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { apiBaseUrl, isDevelopment } from '@/config';
import { isDelegated, isScopedSession, listSharedAgents, switchTo } from '@/utils/accountSwitch';
import PageHeader from './PageHeader.vue';
import Notification from './Notification.vue';
import UpgradeModal from './UpgradeModal.vue';

export default {
  name: 'ChatbotList',
  components: {
    PageHeader,
    Notification,
    UpgradeModal
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const chatbots = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const searchQuery = ref('');
    const statusFilter = ref('all');
    const sortOrder = ref('desc');
    const showAddModal = ref(false);
    const showDeleteModal = ref(false);
    const activeMenu = ref(null);
    const chatbotToDelete = ref(null);
    const deleteConfirmation = ref('');
    const displayLimit = ref(6);
    const newChatbot = ref({
      name: '',
      description: ''
    });
    const showNotification = ref(false);
    const showEditModal = ref(false);
    const editingChatbot = ref(null);
    const notificationMessage = ref('');
    const notificationType = ref('success');
    const showCloneModal = ref(false);
    const chatbotToClone = ref(null);
    const cloneName = ref('');
    const isCloning = ref(false);
    const showCommunityBubble = ref(false);
    const showUpgradeModal = ref(false);

    // Sessions partagées : en session déléguée LIMITÉE à des agents, on masque les
    // actions propriétaire (créer/cloner/supprimer — bloquées serveur de toute façon).
    const delegatedSession = isDelegated();
    const scopedSession = isScopedSession();
    // Agents partagés avec moi (affichés dans MA session uniquement).
    const sharedAgents = ref([]);
    const openingShared = ref(null);
    const openSharedAgent = async (s) => {
      openingShared.value = s.bot._id;
      try {
        await switchTo(s.account_id); // redirige vers /dashboard en session déléguée
      } catch (e) {
        openingShared.value = null;
        notificationMessage.value = e.message || "Impossible d'ouvrir cet agent partagé.";
        notificationType.value = 'error';
        showNotification.value = true;
      }
    };

    // Dev Dump Logic
    const isDev = isDevelopment;
    const showUserDump = ref(false);
    const userDumpData = computed(() => {
        try {
            const data = localStorage.getItem('user');
            return data ? JSON.parse(data) : { error: 'No user data in localStorage' };
        } catch (e) {
            return { error: 'Parse error', raw: localStorage.getItem('user') };
        }
    });

    // Subscription computed properties
    const userSubscription = computed(() => {
      try {
        const userData = localStorage.getItem('user');
        if (!userData) return null;
        const user = JSON.parse(userData);
        return user?.data?.subscription || null;
      } catch (e) {
        return null;
      }
    });

    const maxAgents = computed(() => {
      if (!userSubscription.value) return 1; // Free plan: 1 agent
      return userSubscription.value.max_agents || 1;
    });

    const agentLimitReached = computed(() => {
      // If max_agents is -1, it means unlimited
      if (maxAgents.value === -1) return false;
      return chatbots.value.length >= maxAgents.value;
    });

    // Handle new agent button click
    const handleNewAgent = () => {
      if (agentLimitReached.value) {
        showUpgradeModal.value = true;
      } else {
        router.push('/dashboard/chatbots/create');
      }
    };

    // Handle plan selection from upgrade modal
    const handlePlanSelect = (plan) => {
      console.log('Plan selected:', plan);
      showUpgradeModal.value = false;
      
      // For Enterprise, redirect to contact
      if (plan.slug === 'enterprise') {
        window.open('https://wa.me/221781528375?text=Je%20suis%20intéressé%20par%20le%20plan%20Enterprise', '_blank');
        return;
      }
      
      // For other plans, show notification
      showNotificationMessage(`Vous avez choisi le plan ${plan.name}. Redirection vers le paiement...`, 'success');
      // TODO: Implement payment flow
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showNotificationMessage('JSON copié !', 'success');
    };


    const showNotificationMessage = (message, type) => {
      notificationMessage.value = message;
      notificationType.value = type;
      showNotification.value = true;
    };

    const dismissCommunityBubble = () => {
        showCommunityBubble.value = false;
        localStorage.setItem('hidePeeloCommunity', 'true');
    };

    const formatNumber = (number) => {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    const getChatbotAvatar = (chatbot) => {
      // Handle legacy default avatar path
      if (chatbot.avatar === '/avatars/bot1.png') {
        // Use a nice default bot avatar (e.g. DiceBear)
        // Using bottts-neutral which is clean and professional
        return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${chatbot._id || chatbot.bot_name}`;
      }
      
      // Standard avatar
      if (chatbot.avatar) {
        // Check if it's already a full URL (in case of legacy data)
        if (chatbot.avatar.startsWith('http')) {
          return chatbot.avatar;
        }
        return `https://api.peelo.io/public/${chatbot.avatar}`;
      }
      
      // Return null to fallback to the default icons (like WhatsApp icon)
      return null;
    };

    const fetchChatbots = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        // Récupérer le token depuis localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          error.value = t('chatbotList.errNoUser');
          loading.value = false;
          return;
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        if (!token) {
          error.value = t('chatbotList.errNoToken');
          loading.value = false;
          return;
        }
        
        // Faire la requête avec le token d'authentification
        const response = await axios.get(`${apiBaseUrl}/get/list/chatbots`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.chatbots && response.data.chatbots.length > 0) {
          chatbots.value = response.data.chatbots.map(chatbot => {
            if (!chatbot.update_date && !chatbot.updatedAt && !chatbot.updated_at) {
              return {
                ...chatbot,
                update_date: new Date().toISOString()
              };
            }
            return chatbot;
          });
          
          chatbots.value.sort((a, b) => {
            const dateA = new Date(a.update_date || 0);
            const dateB = new Date(b.update_date || 0);
            
            if (sortOrder.value === 'asc') {
              return dateA - dateB;
            } else {
              return dateB - dateA;
            }
          });
        } else {
          chatbots.value = response.data.chatbots || [];
        }
        
        displayLimit.value = 6;
        
        nextTick(() => {
          initInfiniteScroll();
        });
      } catch (err) {
        //console.error('Erreur lors du chargement des chatbots:', err);
        
        // Gestion des erreurs spécifiques de l'API
        if (err.response) {
          const status = err.response.status;
          const errorData = err.response.data;
          
          switch (status) {
            case 401:
              error.value = t('chatbotList.errSession');
              // Rediriger vers la page de connexion après un délai
              setTimeout(() => {
                localStorage.removeItem('user');
                router.push('/login');
              }, 3000);
              break;
            case 404:
              // Pour les erreurs 404, ne pas définir de message d'erreur
              // mais vider la liste des chatbots pour afficher l'état vide
              error.value = null;
              chatbots.value = []; 
              break;
            case 500:
              error.value = errorData.error || t('chatbotList.errServer');
              break;
            default:
              error.value = errorData.error || t('chatbotList.errLoad', { status });
          }
        } else if (err.request) {
          // La requête a été faite mais aucune réponse n'a été reçue
          error.value = t('chatbotList.errNetwork');
        } else {
          // Une erreur s'est produite lors de la configuration de la requête
          error.value = err.message || t('chatbotList.errGeneric');
        }
      } finally {
        loading.value = false;
      }
    };

    const filteredChatbots = computed(() => {
      let filtered = [...chatbots.value];

      filtered = filtered.filter(chatbot => {
        const matchesSearch = chatbot.bot_name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                            chatbot.description.toLowerCase().includes(searchQuery.value.toLowerCase());
        const matchesStatus = statusFilter.value === 'all' || chatbot.status === statusFilter.value;
        return matchesSearch && matchesStatus;
      });

      filtered.sort((a, b) => {
        const timeA = new Date(a.update_date || 0).getTime();
        const timeB = new Date(b.update_date || 0).getTime();
        return sortOrder.value === 'desc' ? timeB - timeA : timeA - timeB;
      });

      return filtered;
    });

    const displayedChatbots = computed(() => {
      return filteredChatbots.value.slice(0, displayLimit.value);
    });

    const hasMoreChatbots = computed(() => {
      return filteredChatbots.value.length > displayLimit.value;
    });

    const loadMore = () => {
      if (filteredChatbots.value.length > displayLimit.value) {
        displayLimit.value += 6;
      }
    };

    const initInfiniteScroll = () => {
      if (observer) {
        observer.disconnect();
      }

      nextTick(() => {
        const element = document.querySelector('.infinite-scroll-observer');
        if (element) {
          observerElement.value = element;
          
          observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting && hasMoreChatbots.value) {
              loadMore();
            }
          }, {
            root: null,
            rootMargin: '100px',
            threshold: 0.1
          });

          observer.observe(element);
        }
      });
    };

    watch(filteredChatbots, (newFiltered, oldFiltered) => {
      if (newFiltered.length !== oldFiltered?.length) {
        displayLimit.value = 6;
        nextTick(() => {
          initInfiniteScroll();
        });
      }
    }, { deep: false, immediate: false });

    const toggleSortOrder = () => {
      sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
    };

    const toggleMenu = (chatbotId) => {
      activeMenu.value = activeMenu.value === chatbotId ? null : chatbotId;
    };

    const confirmDelete = (chatbot) => {
      chatbotToDelete.value = chatbot;
      showDeleteModal.value = true;
      activeMenu.value = null;
      deleteConfirmation.value = '';
    };

    const deleteChatbot = async () => {
      if (!chatbotToDelete.value) return;

      try {
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          showNotificationMessage('Données utilisateur non trouvées', 'error');
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          showNotificationMessage(t('chatbotList.msgNoToken'), 'error');
          return;
        }

        // Envoyer la requête de suppression
        await axios.delete(
          `${apiBaseUrl}/delete/strong/chatbot/${chatbotToDelete.value._id}`,
          {
            headers: {
              'Authorization': `Bearer ${userObj.data.token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Supprimer le chatbot de la liste locale
        chatbots.value = chatbots.value.filter(c => c._id !== chatbotToDelete.value._id);
        
        // Fermer la modal et réinitialiser
        showDeleteModal.value = false;
        chatbotToDelete.value = null;
        deleteConfirmation.value = '';
        
        // Afficher la notification de succès
        showNotificationMessage(t('chatbotList.msgDeleted'), 'success');
      } catch (err) {
        showNotificationMessage(err.response?.data?.error || t('chatbotList.msgDeleteErr'), 'error');
      }
    };

    const addChatbot = () => {
      if (newChatbot.value.name && newChatbot.value.description) {
        const newId = Math.max(...chatbots.value.map(c => c.id)) + 1;
        chatbots.value.push({
          id: newId,
          ...newChatbot.value,
          conversationCount: 0,
          userCount: 0,
          uptime: 100
        });
        showAddModal.value = false;
        newChatbot.value = { name: '', description: '' };
      }
    };

    const editChatbot = (chatbot) => {
      editingChatbot.value = { ...chatbot };
      showEditModal.value = true;
      activeMenu.value = null;
    };

    const closeModal = () => {
      showEditModal.value = false;
      editingChatbot.value = null;
      activeMenu.value = null;
    };

    const saveChatbot = async () => {
      try {
        if (editingChatbot.value) {
          const userData = localStorage.getItem('user');
          if (!userData) {
            showNotification.value = true;
            notificationType.value = 'error';
            notificationMessage.value = t('chatbotList.msgUserNotFound');
            return;
          }

          let token;
          try {
            const userObj = JSON.parse(userData);
            if (userObj.data && userObj.data.token) {
              token = userObj.data.token;
            } else {
              showNotification.value = true;
              notificationType.value = 'error';
              notificationMessage.value = t('chatbotList.msgBadStruct');
              return;
            }
          } catch (parseError) {
            showNotification.value = true;
            notificationType.value = 'error';
            notificationMessage.value = t('chatbotList.msgBadFormat');
            return;
          }

          await axios.put(
            `${apiBaseUrl}/update/ease/chatbot/${editingChatbot.value._id}`,
            editingChatbot.value,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          showNotification.value = true;
          notificationType.value = 'success';
          notificationMessage.value = t('chatbotList.msgEdited');
          await fetchChatbots();
          closeModal();
        }
      } catch (err) {
        //console.error('Erreur lors de la modification du chatbot:', err);
        showNotification.value = true;
        notificationType.value = 'error';
        notificationMessage.value = err.response?.data?.error || 'Erreur lors de la modification de l\'agent';
      }
    };

    const truncateDescription = (description) => {
      if (!description) return '';
      const maxLength = 100;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength) + '...';
    };

    // Référence à l'élément d'observation pour l'infinite scroll
    const observerElement = ref(null);
    let observer = null;

    const openChatbot = (chatbot, event) => {
      // Éviter la redirection si on clique sur le menu ou ses options
      if (event.target.closest('.menu-btn') || event.target.closest('.chatbot-menu')) {
        return;
      }
      router.push(`/dashboard/chatbots/${chatbot._id}/home`);
    };

    const confirmClone = (chatbot) => {
      chatbotToClone.value = chatbot;
      cloneName.value = `${chatbot.bot_name} (Clone)`;
      showCloneModal.value = true;
      activeMenu.value = null;
    };

    // Partage façon Canva : ouvre la page Équipe pré-scopée sur CET agent.
    const inviteCollaborator = (chatbot) => {
      activeMenu.value = null;
      router.push(`/dashboard/team?agent=${chatbot._id}`);
    };

    const cloneChatbot = async () => {
      if (!chatbotToClone.value || !cloneName.value.trim()) return;

      isCloning.value = true;

      try {
        // Récupérer le token depuis le localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          showNotificationMessage('Données utilisateur non trouvées', 'error');
          return;
        }

        const userObj = JSON.parse(userData);
        if (!userObj.data?.token) {
          showNotificationMessage(t('chatbotList.msgNoToken'), 'error');
          return;
        }

        // Envoyer la requête de clonage
        const response = await axios.post(
          `${apiBaseUrl}/strong/clonefinal/chatbot/${chatbotToClone.value._id}`,
          {
            namebotnew: cloneName.value.trim()
          },
          {
            headers: {
              'Authorization': `Bearer ${userObj.data.token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          // Afficher la notification de succès
          showNotificationMessage(response.data.message || t('chatbotList.msgCloned'), 'success');
          
          // Fermer la modal
          showCloneModal.value = false;
          chatbotToClone.value = null;
          cloneName.value = '';
          
          // Recharger la page après un court délai pour voir la notification
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          throw new Error(response.data.message || 'Erreur lors du clonage');
        }
      } catch (err) {
        //console.error('Erreur lors du clonage de l\'agent:', err);
        showNotificationMessage(
          err.response?.data?.message || err.message || 'Erreur lors du clonage du chatbot', 
          'error'
        );
      } finally {
        isCloning.value = false;
      }
    };

    onMounted(() => {
      fetchChatbots();
      // Agents partagés avec moi (non bloquant, uniquement dans ma propre session).
      if (!delegatedSession) {
        listSharedAgents().then((l) => { sharedAgents.value = l; }).catch(() => {});
      }
      // Check local storage for community bubble
      setTimeout(() => {
          const isDismissed = localStorage.getItem('hidePeeloCommunity');
          if (!isDismissed) {
              showCommunityBubble.value = true;
          }
      }, 1500);
    });

    // Nettoyer l'observer lors de la destruction du composant
    onUnmounted(() => {
      if (observer) {
        observer.disconnect();
      }
    });

    // ==================== DÉPLOIEMENT (Brouillon / En ligne) ====================
    const deployingId = ref(null);

    // Grandfather : un agent sans état de déploiement est considéré EN LIGNE.
    const isDeployed = (chatbot) => {
      return chatbot && chatbot.deployment ? chatbot.deployment.status === 'deployed' : true;
    };

    const getAuthToken = () => {
      try { return (JSON.parse(localStorage.getItem('user') || '{}').data || {}).token || null; }
      catch (_) { return null; }
    };

    const toggleDeploy = async (chatbot) => {
      activeMenu.value = null;
      const token = getAuthToken();
      if (!token) { showNotificationMessage(t('chatbotList.msgSessionExpired'), 'error'); return; }
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const currentlyDeployed = isDeployed(chatbot);
      deployingId.value = chatbot._id;
      try {
        if (currentlyDeployed) {
          await axios.post(`${apiBaseUrl}/strong/bot/${chatbot._id}/undeploy`, {}, { headers });
          chatbot.deployment = { ...(chatbot.deployment || {}), status: 'undeployed' };
          showNotificationMessage(t('chatbotList.msgBackToDraft', { name: chatbot.bot_name }), 'success');
        } else {
          await axios.post(`${apiBaseUrl}/strong/bot/${chatbot._id}/deploy`, { channels: { whatsapp: true, website: true } }, { headers });
          chatbot.deployment = { ...(chatbot.deployment || {}), status: 'deployed' };
          showNotificationMessage(t('chatbotList.msgNowOnline', { name: chatbot.bot_name }), 'success');
        }
      } catch (err) {
        const status = err.response?.status;
        const data = err.response?.data || {};
        if (status === 402 && data.error_type === 'needs_upgrade') {
          // Free -> ouvre la modale d'abonnement (Pricing).
          showUpgradeModal.value = true;
        } else if (status === 402 && data.error_type === 'needs_payment') {
          const amount = (data.amount || 25000).toLocaleString('fr-FR');
          showNotificationMessage(t('chatbotList.msgExtraDeploy', { amount }), 'info');
        } else {
          showNotificationMessage(data.message || data.error || t('chatbotList.msgDeployImpossible'), 'error');
        }
      } finally {
        deployingId.value = null;
      }
    };

    return {
      chatbots,
      loading,
      error,
      delegatedSession,
      scopedSession,
      sharedAgents,
      openingShared,
      openSharedAgent,
      searchQuery,
      statusFilter,
      sortOrder,
      showAddModal,
      showDeleteModal,
      activeMenu,
      chatbotToDelete,
      deleteConfirmation,
      newChatbot,
      displayedChatbots,
      hasMoreChatbots,
      loadMore,
      getChatbotAvatar,
      toggleMenu,
      confirmDelete,
      deleteChatbot,
      addChatbot,
      editChatbot,
      showNotification,
      showEditModal,
      editingChatbot,
      saveChatbot,
      notificationMessage,
      notificationType,
      formatNumber,
      truncateDescription,
      closeModal,
      observerElement,
      toggleSortOrder,
      showNotificationMessage,
      openChatbot,
      showCloneModal,
      chatbotToClone,
      cloneName,
      isCloning,
      confirmClone,
      inviteCollaborator,
      cloneChatbot,
      showCommunityBubble,
      dismissCommunityBubble,
      // Dev Dump
      isDev,
      showUserDump,
      userDumpData,
      copyToClipboard,
      // Subscription gating
      showUpgradeModal,
      userSubscription,
      maxAgents,
      agentLimitReached,
      handleNewAgent,
      handlePlanSelect,
      // Déploiement
      deployingId,
      isDeployed,
      toggleDeploy
    };
  }
};
</script>

<style scoped>
.chatbot-list {
  padding: 24px 16px;
}




/* Search and Filter Section */
.search-filter-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin: 32px 32px 32px;
  padding: 0;
}

.search-box-wrapper {
  flex: 1;
  max-width: 600px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: white;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.02);
  transition: all 0.2s ease;
}

.search-box:focus-within {
  border-color: #1ABB34;
  box-shadow: 0 4px 15px rgba(26, 187, 52, 0.1);
}

.search-box svg {
  color: #6F767E;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  color: #1A1D1F;
  background: transparent;
}

.filter-options-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-select {
  padding: 10px 32px 10px 16px;
  background: white;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1A1D1F;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236F767E' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  min-width: 160px;
  transition: all 0.2s ease;
}

.status-select:hover {
  border-color: rgba(0,0,0,0.15);
}

.sleek-sort-btn, .dev-dump-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: white;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6F767E;
}

.sleek-sort-btn:hover, .dev-dump-btn:hover {
  background: #f8fafc;
  color: #1ABB34;
  border-color: #1ABB34;
}

.sleek-add-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  background: #143028; /* Peelo Dark Green */
  color: #DAFFD1; /* Peelo Light Green */
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.2);
}

.sleek-add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(20, 48, 40, 0.3);
  filter: brightness(1.1);
}

.sleek-add-btn.disabled {
  background: #143028;
  opacity: 1; /* Keep full visibility */
  border: 1px solid rgba(218, 255, 209, 0.3);
  box-shadow: 0 4px 15px rgba(20, 48, 40, 0.4);
}

.sleek-add-btn.disabled:hover {
  background: #1a3d33;
  box-shadow: 0 6px 20px rgba(20, 48, 40, 0.5);
  transform: translateY(-2px);
}

.limit-badge-small {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background: rgba(218, 255, 209, 0.2);
  color: #DAFFD1;
  border-radius: 8px;
  font-size: 11px;
}

@media (max-width: 1024px) {
  .search-filter-section {
    flex-direction: column;
    align-items: stretch;
    margin: 0 16px 24px;
  }
  
  .search-box-wrapper {
    max-width: none;
  }
}

/* Chatbots Grid */
.chatbots-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
}

.chatbot-card {
  background: #FFFFFF;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.chatbot-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #1ABB34;
}

.chatbot-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
}

.chatbot-icon {
  width: 40px;
  height: 40px;
  background: #E8F8EA;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1ABB34;
}

.chatbot-info {
  flex: 1;
}

.chatbot-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.status-icon {
  display: flex;
  align-items: center;
}

.status-icon svg {
  width: 10px;
  height: 10px;
}

.status-icon.active svg {
  color: #1ABB34;
}

.status-icon.inactive svg {
  color: #BE123C;
}

.status-icon.active svg path {
  fill: currentColor;
}

.status-icon.inactive svg path {
  fill: currentColor;
  opacity: 0.9;
}

.chatbot-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.chatbot-info p {
  font-size: 13px;
  color: #6F767E;
  margin: 0;
}

.chatbot-actions {
  position: relative;
  z-index: 2;
}

.menu-btn {
  background: none;
  border: none;
  padding: 8px;
  color: #6F767E;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.menu-btn:hover {
  background: #E8ECEF;
  color: #1A1D1F;
}

.chatbot-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 100;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
  background: none;
  border: none;
  color: #1A1D1F;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background: #E8ECEF;
}

.menu-item.delete {
  color: #FF3B30;
}

.menu-item.delete:hover {
  background: #FFD5D5;
}

.chatbot-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-top: 20px;
  border-top: 1px solid #E8ECEF;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.stat:hover {
  background: #F9FAFB;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  letter-spacing: 0.5px;
  padding: 0 4px;
}

.stat-label {
  font-size: 13px;
  color: #6F767E;
  font-weight: 500;
  text-align: center;
}

.stat:last-child .stat-value {
  font-size: 14px;
  padding: 4px 8px;
  background: #E8F8EA;
  color: #1ABB34;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0;
}

.stat:last-child:hover .stat-value {
  background: #1ABB34;
  color: white;
  box-shadow: 0 0 12px rgba(26, 187, 52, 0.3);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 26, 20, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #fff;
  border: 1px solid rgba(20, 48, 40, 0.08);
  border-radius: 24px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 40px 90px -40px rgba(20, 48, 40, 0.5);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 24px;
  border-bottom: 1px solid rgba(20, 48, 40, 0.07);
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #143028;
  margin: 0;
}

.close-btn {
  background: rgba(20, 48, 40, 0.05);
  border: none;
  padding: 8px;
  color: #143028;
  cursor: pointer;
  border-radius: 9999px;
  display: flex;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(20, 48, 40, 0.12);
  color: #143028;
}

.modal-body {
  padding: 22px 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(20, 48, 40, 0.55);
  margin-bottom: 7px;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 12px;
  font-size: 0.95rem;
  color: #143028;
  background: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.08);
}

.form-group textarea {
  height: 100px;
  resize: vertical;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 24px 22px;
  border-top: 1px solid rgba(20, 48, 40, 0.07);
}

.cancel-btn,
.save-btn,
.delete-btn {
  padding: 11px 20px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid rgba(20, 48, 40, 0.18);
  color: #143028;
}

.cancel-btn:hover {
  background: rgba(20, 48, 40, 0.05);
}

.save-btn {
  background: #143028;
  border: none;
  color: #fff;
}

.save-btn:hover {
  background: #1b4538;
  transform: scale(1.02);
}

.save-btn:disabled {
  background: rgba(20, 48, 40, 0.35);
  cursor: not-allowed;
  opacity: 0.7;
}

.save-btn:disabled:hover {
  background: rgba(20, 48, 40, 0.35);
  transform: none;
}

.delete-btn {
  background: #FF3B30;
  border: none;
  color: white;
}

.delete-btn:hover {
  background: #FFD5D5;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 64px 0;
}

.empty-state svg {
  color: #6F767E;
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 16px;
  color: #6F767E;
  margin: 0 0 24px 0;
}

.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #1ABB34;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn:hover {
  background: #159B2B;
  transform: translateY(-1px);
}

@media (max-width: 1400px) {
  .chatbots-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .chatbot-list {
    padding: 16px;
  }

  .chatbots-grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }
}

.form-group input.error {
  border-color: #FF3B30;
}

.error-message {
  display: block;
  color: #FF3B30;
  font-size: 12px;
  margin-top: 4px;
}

.delete-btn:disabled {
  background: #FFA5A5;
  cursor: not-allowed;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #F9FAFB;
  border-radius: 12px;
  margin: 20px 0;
}

.loading-state {
  color: #6F767E;
}

.loading-state p {
  margin-top: 16px;
  font-size: 16px;
}

.error-state {
  color: #EF4444;
  border: 1px dashed #FECACA;
  background-color: #FEF2F2;
}

.error-state svg {
  margin-bottom: 16px;
  color: #EF4444;
}

.error-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #B91C1C;
}

.error-state p {
  font-size: 16px;
  margin-bottom: 24px;
  max-width: 600px;
  color: #991B1B;
}

.retry-btn {
  padding: 8px 20px;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #1F2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-btn:hover {
  background-color: #F3F4F6;
  transform: translateY(-1px);
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1ABB34;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.advanced-options {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(20, 48, 40, 0.07);
}

.chatbot-link {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #143028;
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 700;
  padding: 12px 14px;
  border: 1px solid rgba(20, 48, 40, 0.12);
  border-radius: 12px;
  background: #FCFAF7;
  transition: all 0.2s ease;
}

.chatbot-link span { flex: 1; }

.chatbot-link:hover {
  color: #143028;
  border-color: rgba(20, 48, 40, 0.3);
  background: rgba(20, 48, 40, 0.04);
}

.chatbot-link svg {
  transition: transform 0.2s ease;
}

.chatbot-link:hover svg:last-child {
  transform: translateX(3px);
}

select {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 12px;
  font-size: 0.95rem;
  color: #143028;
  background: #fff;
  cursor: pointer;
}

select:focus {
  border-color: #143028;
  outline: none;
}

/* Infinite Scroll Observer */
.infinite-scroll-observer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  margin-top: 20px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1ABB34;
}

.loading-spinner svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}


/* Community Bubble */
.community-bubble {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 340px;
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 100;
  display: flex;
  flex-direction: column;
}

.close-bubble {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-bubble:hover {
  background: #f3f4f6;
  color: #4b5563;
}

.bubble-content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.bubble-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f0fdf4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.bubble-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bubble-text h4 {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.bubble-text p {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

.join-btn {
  display: inline-block;
  background: #128C7E;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.join-btn:hover {
  background: #075E54;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(18, 140, 126, 0.2);
}

/* Slide Transition */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
/* Sleek Card Design */
.sleek-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
}

.sleek-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  height: 100%;
}

.sleek-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.08);
}

.card-cover {
  height: 120px;
  background: #f8f9fa;
  position: relative;
  background-size: cover;
  background-position: center;
}

.whatsapp-theme .card-cover {
  position: relative;
}
/* Overlay removed as requested */

.card-avatar-wrapper {
  margin-top: -32px;
  margin-left: 24px;
  position: relative;
  z-index: 2;
  width: 64px;
  height: 64px;
}

.card-avatar {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: white;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 12px;
  color: #64748b;
}

.avatar-fallback.whatsapp {
  background: #25D366;
  color: white;
}

.card-actions-top {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
}

.glass-btn {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
}

.glass-btn:hover {
  background: white;
  color: #334155;
  transform: scale(1.05);
}

.card-body {
  padding: 12px 24px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.card-title-row h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.status-badge-sleek {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #64748b;
}

.status-badge-sleek.active {
  background: #dcfce7;
  color: #166534;
}

.card-desc {
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  margin-bottom: 16px;
  flex: 1;
}

.card-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.tag {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tag.whatsapp-tag {
  background: #dcfce7;
  color: #15803d;
}

.card-footer {
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafaf9;
}

.config-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.config-btn:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
  transform: translateY(-1px);
}

.footer-stats-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.footer-divider {
  width: 1px;
  height: 12px;
  background: #cbd5e1;
}

.connected-dot {
  width: 8px;
  height: 8px;
  background: #25D366;
  border-radius: 50%;
  box-shadow: 0 0 0 2px #dcfce7;
}

/* Dark Mode Overrides */
:global(.dark-mode) .sleek-card {
  background: #1e293b;
  border-color: #334155;
  box-shadow: 0 4px 24px rgba(0,0,0,0.2);
}

:global(.dark-mode) .card-cover {
  background-color: #0f172a;
  background-image: radial-gradient(#334155 1px, transparent 1px);
}

:global(.dark-mode) .card-header-row h3 {
  color: #f1f5f9;
}

:global(.dark-mode) .card-desc {
  color: #94a3b8;
}

:global(.dark-mode) .card-footer {
  background: #0f172a;
  border-color: #334155;
}

:global(.dark-mode) .config-btn {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .config-btn:hover {
  background: #334155;
}

:global(.dark-mode) .card-avatar {
  background: #1e293b;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

:global(.dark-mode) .avatar-fallback {
  background: #334155;
}

/* ==================== Déploiement (badge + bouton) ==================== */
.deploy-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}
.deploy-badge .deploy-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}
.deploy-badge.online {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.deploy-badge.online .deploy-dot { background: #10b981; }
.deploy-badge.draft {
  background: rgba(100, 116, 139, 0.14);
  color: #64748b;
}
.deploy-badge.draft .deploy-dot { background: #94a3b8; }

.deploy-btn {
  padding: 7px 16px;
  border-radius: 8px;
  border: none;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  background: #2563eb;
  color: #fff;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.deploy-btn:hover { background: #1d4ed8; }
.deploy-btn.is-online {
  background: transparent;
  color: #64748b;
  border: 1px solid #cbd5e1;
}
.deploy-btn.is-online:hover { background: rgba(100, 116, 139, 0.08); }
.deploy-btn:disabled { opacity: 0.6; cursor: default; }

/* ==================== PARTAGÉS AVEC MOI ==================== */
/* padding-bottom : évite que la dernière carte reste coincée sous la bulle WhatsApp flottante */
.shared-section { margin-top: 42px; padding-bottom: 90px; }
.shared-head { display: flex; align-items: center; gap: 10px; color: #111827; }
.shared-head h3 { font-size: 17px; font-weight: 700; letter-spacing: -.01em; margin: 0; }
.shared-head svg { color: #143028; }
.shared-count {
  background: #eef4f1; color: #1f5140; font-size: 12px; font-weight: 700;
  padding: 2px 9px; border-radius: 20px;
}
.shared-sub { margin: 6px 0 16px; font-size: 13.5px; color: #6b7280; }
.shared-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.shared-card {
  display: flex; align-items: center; gap: 13px; text-align: left; cursor: pointer;
  border: 1.5px solid #eceef1; background: #fff; border-radius: 15px; padding: 13px 15px;
  transition: border-color .14s, box-shadow .14s, transform .08s;
}
.shared-card:hover { border-color: #c9d0d8; box-shadow: 0 6px 18px -8px rgba(17, 24, 39, .18); }
.shared-card:active { transform: translateY(1px); }
.shared-card:disabled { opacity: .65; cursor: default; }
.shared-avatar-wrap { position: relative; flex-shrink: 0; }
.shared-avatar {
  width: 46px; height: 46px; border-radius: 13px; overflow: hidden; background: #eef1f4;
  display: flex; align-items: center; justify-content: center; color: #52606d; font-size: 18px; font-weight: 700;
}
.shared-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.shared-status-dot {
  position: absolute; right: -2px; bottom: -2px; width: 13px; height: 13px; border-radius: 50%;
  border: 2.5px solid #fff;
}
.shared-status-dot.active { background: #22c55e; }
.shared-status-dot.inactive { background: #cbd2da; }
.shared-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.shared-name { font-size: 15px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shared-owner { font-size: 12.5px; color: #9199a3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shared-open {
  flex-shrink: 0; font-size: 13px; font-weight: 600; color: #143028;
  background: #eef4f1; padding: 7px 14px; border-radius: 9px;
}
.shared-card:hover .shared-open { background: #143028; color: #DAFFD1; }

:global(.dark-mode) .shared-head { color: #eef2f0; }
:global(.dark-mode) .shared-head svg { color: #DAFFD1; }
:global(.dark-mode) .shared-count { background: #1e2f28; color: #DAFFD1; }
:global(.dark-mode) .shared-card { background: #16211d; border-color: #24332d; }
:global(.dark-mode) .shared-card:hover { border-color: #3a4c44; }
:global(.dark-mode) .shared-avatar { background: #24332d; color: #9aa5a1; }
:global(.dark-mode) .shared-status-dot { border-color: #16211d; }
:global(.dark-mode) .shared-name { color: #eef2f0; }
:global(.dark-mode) .shared-open { background: #1e2f28; color: #DAFFD1; }
</style> 